import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import React, { ComponentProps, MouseEventHandler, ReactElement, useRef, useState } from 'react';
import SVGIcon from '../../components/SVGIcons';
import { convertModuleName } from '../../utils/getModuleName';
import componentStyles from 'styles/partials/_form.css';
import getIn from '../../components/connectedForm/utils/getIn';
import fullMenuStylesCSS from 'styles/partials/_fullMenu.css';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import setDocumentTitle from '../../utils/SetDocumentTitle';
import { useHistory, useParams } from 'react-router';
import styled from 'styled-components';
import getClientMutationId from '../../utils/getClientMutationId';
import useMenuOptions, { IMenuEntry } from '../../components/MenuOptions/useMenuOptions';
import BetaTag from '../../components/BetaTag';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { IReduxStore } from '../../@types/reduxTypes';
import { IFolderInput, IScreenInput, Maybe } from '../../graphql/schema/p4types';
import { Pathname } from 'history';
import { IAppMenu } from '../../@types/menuTypes';

const ReportContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const ReportSection = styled.div`
  padding-top: 20px;
  padding-right: 60px;
  width: 300px;
`;

const ReportOptions = styled.div`
  background-color: #faf9f8;
  margin-top: 8px;
  margin-bottom: 20px;
  border-bottom: none;
  border-top: 1px solid #eee;
  border-left: 1px solid #eee;
  border-right: 1px solid #eee;
  box-shadow: 0 1px 0 #d4d7dc, -1px -1px 0 #eceef1, 1px 0 0 #eceef1;
`;

const ReportSectionTitle = styled.div`
  font-size: 16px;
  color: #212b36;
  font-weight: 500;
`;

const ReportOption = styled(Link)`
  padding: 4px 8px;
  margin: 2px 0;
  font-size: 14px;
  color: #495057;
  border: none;
  border-bottom: 1px solid #eee;
  font-weight: 400;
  white-space: nowrap;
  cursor: pointer;
`;

const useFullMenuStyles = makeStyles({
  pageBackground: {
    background: p4Tokens.colorNeutralBackground1,
    color: p4Tokens.colorNeutralForeground3,
  },
  pageHeader: {
    color: '#212b36',
    fontSize: '22px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  pageHeaderIcon: {
    fontSize: '22px !important',
    color: '#bdbdbd',
    verticalAlign: 'bottom !important',
    lineHeight: '30px',
    paddingRight: '5px',
    cursor: 'pointer',
  },
  pageHeaderNav: {
    color: '#bdbdbd',
    cursor: 'pointer',
  },
  linkBox: {
    float: 'left',
    margin: '16px',
    height: '140px',
    width: '150px',
    textAlign: 'center',
    cursor: 'pointer',
    position: 'relative',
    borderBottom: '4px solid transparent',
    left: '-25px',
    color: p4Tokens.colorNeutralStroke1Hover,
    ':hover': {
      background: p4Tokens.colorNeutralBackground1Hover,
      borderBottom: '4px solid #00b9af',
    },
    '& a': {
      color: p4Tokens.colorNeutralForeground2,
      ':hover': {
        textDecoration: 'none',
        color: p4Tokens.colorNeutralForeground2,
      },
    },
  },
  linkTitle: {
    padding: '8px 12px 0 12px',
    fontSize: '14px',
    color: p4Tokens.colorBrandMenuForeground5,
  },
  configLink: {
    position: 'relative',
    '& a': {
      color: p4Tokens.colorNeutralForeground4,
      padding: '4px 0 4px 8px',
      marginRight: '4px',
      borderLeft: '4px solid transparent',
      fontSize: '14px',
      ':hover': {
        textDecoration: 'none',
        backgroundColor: p4Tokens.colorNeutralBackground4,
        color: p4Tokens.colorNeutralForeground2,
        borderLeft: '4px solid #00b9af',
      },
    },
  },
  favIcon: {
    position: 'absolute',
    right: 0,
    top: 0,
    opacity: 0.8,
    cursor: 'pointer',
    ':hover': {
      color: '#00b9af',
      opacity: 0.6,
    },
  },
  svgIcon: {
    marginTop: '10px',
    marginLeft: '40px',
    height: '75px',
    width: '75px',
  },
  submenuIcon: {
    fontSize: '36px',
    position: 'absolute',
    left: '59px',
    top: '31px',
    color: '#1a237e',
  },
  selected: {
    color: '#00b9af',
    opacity: 1,
    ':hover': { opacity: 0.6, color: '#00b9af' },
  },
  titleHeader: {
    height: '35px',
    backgroundColor: p4Tokens.colorNeutralBackground6,
    color: p4Tokens.colorNeutralForeground4,
    padding: '5px',
    margin: '5px',
  },
});

const getSubMenu = (menu: IAppMenu, subMenuParams: IFullMenuParams) => {
  let newParams;
  const moduleMenu = menu[subMenuParams.module];
  if (Array.isArray(moduleMenu)) {
    for (let i = 0; i < moduleMenu.length; i++) {
      const subMenu = moduleMenu[i];
      if (!subMenu.title) {
        const keys = Object.keys(subMenu);
        if (keys[0] === subMenuParams.submodule) {
          newParams = [subMenuParams.module, i, subMenuParams.submodule];
          break;
        }
      }
    }
  }
  return getIn(menu, newParams);
};

const MenuLink = ({
  onClickFavourite,
  selected,
  icon,
  beta,
  title,
}: {
  onClickFavourite: MouseEventHandler;
  selected: boolean;
  icon: string;
  beta: boolean;
  title: string;
}) => {
  const [hover, setHover] = useState(false);

  const styles = useFullMenuStyles();

  return (
    <div className={styles.linkBox} onMouseEnter={(event) => setHover(true)} onMouseLeave={(event) => setHover(false)}>
      {selected || hover ? (
        <i onClick={onClickFavourite} className={mergeClasses('icon', styles.favIcon, selected && styles.selected)}>
          star
        </i>
      ) : null}
      <div className={styles.svgIcon}>
        <SVGIcon icon={icon} height={75} width={75} fillColor={p4Tokens.colorNeutralForeground3} />
      </div>
      <div className={styles.linkTitle}>{title}</div>
      {beta ? <BetaTag /> : null}
    </div>
  );
};

const SubmenuLink = ({ onClick, title }: { onClick: MouseEventHandler; title: string }) => {
  const styles = useFullMenuStyles();

  return (
    <div className={styles.linkBox} onClick={onClick}>
      <div className={styles.svgIcon}>
        <i className={mergeClasses('icon', styles.submenuIcon)}>apps</i>
        <SVGIcon icon="folder" height={130} width={130} />
      </div>
      <div className={styles.linkTitle}>{title}</div>
    </div>
  );
};

const ConfigLink = ({
  onClickFavourite,
  title,
  selected,
  target,
}: {
  onClickFavourite: MouseEventHandler;
  title?: string;
  selected: boolean;
  target: ComponentProps<typeof Link>['to'];
}) => {
  const [hover, setHover] = useState(false);

  const styles = useFullMenuStyles();

  return (
    <div
      className={styles.configLink}
      onMouseEnter={(event) => setHover(true)}
      onMouseLeave={(event) => setHover(false)}
    >
      <Link to={target}>{title}</Link>
      {hover || selected ? (
        <i onClick={onClickFavourite} className={mergeClasses('icon', styles.favIcon, selected && styles.selected)}>
          star
        </i>
      ) : null}
    </div>
  );
};

type IFullMenuParams = { module: keyof IAppMenu; submodule?: string };

const FullMenu = ({
  menu: propsMenu,
}: {
  menu: { menu: { [key: string]: IScreenInput }; folders: Array<IFolderInput> };
}) => {
  const menuOptions = useMenuOptions();
  const params = useParams<IFullMenuParams>();
  const mainSection = useRef<Array<ReactElement> | undefined>();
  const configSection = useRef<Array<ReactElement> | undefined>();
  const dispatch = useDispatch();
  const history = useHistory();
  const [configOpen, setConfigOpen] = useState(false);

  const fullMenuStyles = useFullMenuStyles();

  const { module: thisModule, submodule } = params;

  const appMenu = useSelector((store: IReduxStore) => store.app.menu);

  const menu: IAppMenu = menuOptions.resetMenuOptions(appMenu);
  const menuModule = params.submodule ? getSubMenu(menu, params) : menu[params.module];

  setDocumentTitle(`${convertModuleName(params.module)} Menu`);

  const navigate = (path: Pathname) =>
    history.push({
      pathname: path,
      state: { previousLocation: history.location?.pathname || 'back' },
    });

  const toggleConfig = () => {
    let open = true;
    if (configOpen) {
      open = false;
    }
    setConfigOpen(open);
  };

  const createMenuPage = () => {
    const menuLinks: Array<ReactElement> = [];
    const menuLevels: Array<ReactElement> = [];

    configSection.current = undefined;
    const workingLevel = menuModule;
    if (!workingLevel) {
      return;
    }

    for (let i = 0; i < workingLevel.length; i++) {
      let menuCount = 1;
      const _menu: Array<keyof typeof propsMenu.menu> =
        Object.keys(propsMenu.menu).length !== 0 ? Object.keys(propsMenu.menu) : [];
      const folders: Array<any> = Object.keys(propsMenu.folders).length !== 0 ? Object.keys(propsMenu.folders) : [];

      for (const __menu of _menu) {
        if (propsMenu.menu[__menu] && !propsMenu.menu[__menu].folder) {
          menuCount++;
        }
      }

      for (const folder of folders) {
        if (propsMenu.folders[folder]) {
          menuCount++;
        }
      }

      if (workingLevel[i].url) {
        let menuLinkTitle = workingLevel[i].title;
        if (workingLevel[i].shortTitle) {
          menuLinkTitle = workingLevel[i].shortTitle;
        }
        let shouldSave = true;
        const screenReference = Array.isArray(workingLevel[i].screenReference)
          ? `[${workingLevel[i].screenReference.join(', ')}]`
          : workingLevel[i].screenReference;
        const pos = getIn(propsMenu, ['menu', screenReference]);
        let id = null;
        const folder = null;
        let concurrencyVersion = null;
        if (pos) {
          shouldSave = false;
          id = pos.id;
          concurrencyVersion = pos.concurrencyVersion;
        }

        const target = {
          pathname: workingLevel[i].url,
          state: { previousLocation: history.location?.pathname || 'back' },
        };
        menuLinks.push(
          <Link key={`links${i}`} to={target}>
            <MenuLink
              onClickFavourite={
                addAndRemove.bind(
                  null,
                  screenReference,
                  folder,
                  menuCount,
                  shouldSave,
                  id,
                  concurrencyVersion
                ) as unknown as MouseEventHandler
              }
              selected={!!pos}
              icon={workingLevel[i].icon}
              beta={workingLevel[i].beta}
              title={menuLinkTitle}
            />
          </Link>
        );
      } else {
        const sectionHeader = Object.keys(workingLevel[i]);
        if (sectionHeader[0] === 'Configuration') {
          createConfiguration(workingLevel[i][sectionHeader[0]], menuCount);
        } else {
          const menuKey = Object.keys(menuModule[i]);

          menuLevels.push(
            <SubmenuLink
              key={`links${i}`}
              onClick={(e) => navigate(`/menu/${thisModule}/${menuKey[0]}`)}
              title={sectionHeader[0].split(/(?=[A-Z])/).join(' ')}
            />
          );
        }
      }
    }
    menuLinks.push(...menuLevels);
    mainSection.current = menuLinks;
  };

  const renderReportOptions = (option: string) => {
    const options: Array<IMenuEntry> = menuModule?.[0]?.[option] || [];
    const reports: Array<any> = [];
    options.forEach((report) => {
      const target = {
        pathname: report.url,
        state: { previousLocation: history.location?.pathname || 'back' },
      };
      reports.push(<ReportOption to={target}>{report.title}</ReportOption>);
    });
    return reports;
  };

  const createConfiguration = (configurationMenu: Array<IMenuEntry>, menuCount: number) => {
    const configOptions: Array<ReactElement> = [];
    for (let i = 0; i < configurationMenu.length; i++) {
      let shouldSave = true;
      const pos = getIn(propsMenu, ['menu', configurationMenu[i].screenReference]);
      let id = null;
      const folder = null;
      let concurrencyVersion = null;
      if (pos) {
        shouldSave = false;
        id = pos.id;
        concurrencyVersion = pos.concurrencyVersion;
      }
      const ref = configurationMenu[i].screenReference!;
      let configLinkTitle = configurationMenu[i].title;
      if (configurationMenu[i].shortTitle) {
        configLinkTitle = configurationMenu[i].shortTitle;
      }
      const target = {
        pathname: configurationMenu[i].url,
        state: { previousLocation: history.location?.pathname || 'back' },
      };
      configOptions.push(
        <ConfigLink
          key={`config${i}`}
          onClickFavourite={
            addAndRemove.bind(
              null,
              ref,
              folder,
              menuCount,
              shouldSave,
              id,
              concurrencyVersion
            ) as unknown as MouseEventHandler
          }
          title={configLinkTitle}
          selected={!!pos}
          target={target}
        />
      );
    }
    configSection.current = configOptions;
  };

  const addAndRemove = (
    screenReference: string,
    folder: Maybe<IFolderInput>,
    menuCount?: number,
    shouldSave?: boolean,
    id?: number,
    concurrencyVersion?: number,
    event?: MouseEvent
  ) => {
    event?.preventDefault();
    const clientMutationId = getClientMutationId();

    if (shouldSave) {
      menuOptions.addRemoveCustomScreen({
        input: {
          clientMutationId: clientMutationId,
          screen: {
            id: id,
            concurrencyVersion: concurrencyVersion,
            sortOrder: menuCount,
            userScreenName: '',
            screenReference: screenReference,
            deleted: false,
            folder: folder,
          },
        },
      });
    } else {
      const folderArray: Array<IFolderInput> = [];
      const screenArray: Array<IScreenInput> = [];
      const tempFolders = propsMenu.folders;
      const tempScreens = propsMenu.menu;
      const removedSortNumber = tempScreens[screenReference].sortOrder;
      const inFolder = !!tempScreens[screenReference].folder;
      tempScreens[screenReference].deleted = true;
      for (const property in tempFolders) {
        if (!inFolder) {
          if (tempFolders[property].sortOrder > removedSortNumber) {
            tempFolders[property].sortOrder--;
          }
        }
        folderArray.push(tempFolders[property]);
      }

      for (const property in tempScreens) {
        if (
          inFolder &&
          tempScreens[property].folder &&
          tempScreens[screenReference].folder?.id === tempScreens[property].folder?.id
        ) {
          if (
            (!inFolder && !tempScreens[property].folder) ||
            (inFolder &&
              tempScreens[property].folder &&
              tempScreens[screenReference].folder?.id === tempScreens[property].folder?.id)
          ) {
            if (tempScreens[property].sortOrder > removedSortNumber) {
              tempScreens[property].sortOrder--;
            }
          }
        }
        screenArray.push(tempScreens[property]);
      }

      menuOptions.addRemoveCustomFolder({
        input: {
          clientMutationId: clientMutationId,
          folders: normalizeVariables(folderArray),
          screens: normalizeVariables(screenArray),
        },
      });
    }
  };

  let configBox = '';
  let extra = '';
  let iconChange = 'rotatedIcon';
  let hidden = '';

  createMenuPage();
  if (configOpen) {
    configBox = 'closed';
    extra = 'open';
    iconChange = 'notRotatedIcon';
    hidden = 'hidden';
  }

  const menuHeight = window.innerHeight - 110;
  return (
    <div className={fullMenuStyles.pageBackground}>
      <div className={mergeClasses(componentStyles.hdrHeading, fullMenuStyles.pageHeader)}>
        <i onClick={(e) => navigate('/')} className={`${fullMenuStyles.pageHeaderIcon} icon`}>
          home
        </i>
        <span onClick={(e) => navigate(`/menu/${thisModule}`)}>{convertModuleName(thisModule)}</span>
        {submodule ? <span className={fullMenuStyles.pageHeaderNav}> / {convertModuleName(submodule)}</span> : null}
      </div>
      {params && params.module === 'Reports' ? (
        <div className={fullMenuStylesCSS.fullPageHolder}>
          <div
            className={`${fullMenuStylesCSS.fullWidth} ${fullMenuStylesCSS[extra]} ${fullMenuStylesCSS.transitionSpeed}`}
            style={{ height: `${menuHeight}px` }}
          >
            <ReportContainer>
              <ReportSection>
                <ReportSectionTitle>Financial</ReportSectionTitle>
                <ReportOptions>{renderReportOptions('Financial')}</ReportOptions>
              </ReportSection>
              <ReportSection>
                <ReportSectionTitle>Customers</ReportSectionTitle>
                <ReportOptions>{renderReportOptions('Customers')}</ReportOptions>
              </ReportSection>
              <ReportSection>
                <ReportSectionTitle>Suppliers</ReportSectionTitle>
                <ReportOptions>{renderReportOptions('Suppliers')}</ReportOptions>
              </ReportSection>
              <ReportSection>
                <ReportSectionTitle>VAT</ReportSectionTitle>
                <ReportOptions>{renderReportOptions('Vat')}</ReportOptions>
              </ReportSection>
              <ReportSection>
                <ReportSectionTitle>Products</ReportSectionTitle>
                <ReportOptions>{renderReportOptions('Products')}</ReportOptions>
              </ReportSection>
            </ReportContainer>
          </div>
        </div>
      ) : (
        <div className={fullMenuStylesCSS.pageHolder}>
          <div
            className={`${fullMenuStylesCSS.leftSide} ${fullMenuStylesCSS[extra]} ${fullMenuStylesCSS.transitionSpeed}`}
            style={{ height: `${menuHeight}px` }}
          >
            <div className={fullMenuStylesCSS.optionsBox}>{mainSection.current}</div>
          </div>
          {Array.isArray(configSection.current) && configSection.current.length > 0 ? (
            <div
              className={`${fullMenuStylesCSS.rightSide} ${fullMenuStylesCSS[configBox]} ${fullMenuStylesCSS.transitionSpeed}`}
            >
              <div className={fullMenuStyles.titleHeader}>
                <i
                  onClick={toggleConfig}
                  className={`${'icon'} ${fullMenuStylesCSS[iconChange]} ${fullMenuStylesCSS.transitionSpeed}`}
                >
                  chevron_left
                </i>
                <span className={fullMenuStylesCSS[hidden]}>Configuration</span>
              </div>
              <div className={fullMenuStylesCSS.configBox}>{configSection.current}</div>
            </div>
          ) : null}
        </div>
      )}
      <div className={fullMenuStylesCSS.clear} />
    </div>
  );
};

export default FullMenu;
